import { useEffect, useRef, useState } from "react";

import { UsbDeviceList } from "../@types/usb";

export interface UseUsbDevicesReturn {
  devices: UsbDeviceList;
  isLoading: boolean;
  error: string | null;
  isSupported: boolean;
}

/**
 * Checks if USB detection is available in the current environment.
 * Use this function to feature-gate USB-related UI components.
 *
 * @returns true if both getUsbDevices and onUsbDeviceUpdate are available
 */
export const isUsbDetectionAvailable = (): boolean => {
  return !!(
    window.electronAPI &&
    typeof window.electronAPI.getUsbDevices === "function" &&
    typeof window.electronAPI.onUsbDeviceUpdate === "function"
  );
};

/**
 * Helper to safely set error state.
 */
const getErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  }
  return "Failed to get USB devices";
};

/**
 * Custom hook for managing USB device detection in Electron environment.
 *
 * @returns object containing devices array, loading state, and error message
 */
export const useUsbDevices = (): UseUsbDevicesReturn => {
  const [devices, setDevices] = useState<UsbDeviceList>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSupported] = useState(isUsbDetectionAvailable());
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    if (!isUsbDetectionAvailable()) {
      setError("USB detection not available");
      setIsLoading(false);
      return;
    }

    const loadInitialDevices = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const currentDevices = await window.electronAPI.getUsbDevices();

        if (isMountedRef.current) {
          setDevices(currentDevices);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(getErrorMessage(err));
          console.error("Error getting USB devices:", err);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    void loadInitialDevices();

    const handleDeviceUpdate = (updatedDevices: UsbDeviceList) => {
      if (isMountedRef.current) {
        setDevices(updatedDevices);
        setError(null);
      }
    };

    const cleanup = window.electronAPI.onUsbDeviceUpdate(handleDeviceUpdate);

    return () => {
      isMountedRef.current = false;
      cleanup?.();
    };
  }, []);

  return {
    devices,
    isLoading,
    error,
    isSupported,
  };
};
